/**
 * The CAD viewer: the dome, the spoke plate, and the hardware that changed
 * between V1 and V2.
 *
 * This is the argument of the whole project in one control. V1 held the plate
 * on with four machine screws; after the first fitting they became countersunk
 * disc magnets, and the thing went from a two-minute job with a screwdriver to
 * a click. Being able to switch between them and pull them apart is worth more
 * than a paragraph describing it.
 *
 * Geometry is parametric, in millimetres, matching the Onshape model: the dome
 * is a hollow spherical cap lathed from the measured radius, the plate is an
 * extruded four-spoke shape, and the posts stand on a 42mm ring. Real GLB
 * exports would replace the stand-ins without changing anything else here.
 *
 * Three.js is loaded on demand and only once the block is actually near the
 * viewport — the page's LCP budget does not survive a second eager 3D bundle,
 * and most readers never scroll this far.
 */

import { asset } from '@remidus/web-core'

import { claimAnchor } from '../anchors'
import type { BlockRenderer } from '@remidus/web-core'

const RED_PRINT = 0xd42a2c
const BLUE = 0x2f6bff

// The millimetre constants that used to drive this file are gone with the
// stand-ins they described. The parts carry their own dimensions now, and a
// second set of numbers here would be one to keep in step with the CAD.

/** The part list, in the same units and the same words as the copy above it.
 *  Rendered as markup so the specification is readable without WebGL, and kept
 *  as the legend once the model loads. */
const PARTS: [string, string][] = [
  ['Head shell', 'The helmet, cut from the approximate head, 3 mm wall'],
  ['Posts', 'Four, on a 42 mm ring'],
  ['Base plate', 'Carries the machine, prints without supports'],
  ['Version one', 'Four screws through the plate into the posts'],
  ['Version two', 'Countersunk neodymium disc magnets, both faces'],
]

export const renderViewer: BlockRenderer = (block) => viewerElement(block.data ?? {})

/** The viewer itself, given a block's data.
 *
 *  Split out from the block renderer so a timeline step can mount one in its
 *  own card: the model belongs in the sequence rather than beside it, and a
 *  step that carries it should be a step like any other — numbered, on the
 *  spine, anchored from its own title. `anchor: false` is what lets it be,
 *  since the step has already claimed the id. */
export function viewerElement(
  data: Record<string, unknown>,
  options: { anchor?: boolean } = {},
): HTMLElement {
  // Editable in the admin. The constant above is the default for a block that
  // has never been edited, not a hard-coded list.
  const parts: [string, string][] = Array.isArray(data.parts)
    ? (data.parts as { name?: string; detail?: string }[])
        .filter((row) => row?.name)
        .map((row) => [String(row.name), String(row.detail ?? '')])
    : PARTS

  const el = document.createElement('figure')
  el.className = 'skin-viewer'

  // A heading turns the block into the page's summary card: the bubble
  // surface, a kicker and title, and the summary prose above the stage.
  // Without one it stays the bare figure it always was.
  const heading = typeof data.heading === 'string' ? data.heading : ''
  if (heading) {
    el.classList.add('skin-bubble-surface', 'skin-viewer--card')
    if (typeof data.kicker === 'string' && data.kicker) {
      const kicker = document.createElement('p')
      kicker.className = 'skin-pullout__kicker'
      kicker.textContent = data.kicker
      el.append(kicker)
    }
    const title = document.createElement('h2')
    title.className = 'skin-viewer__heading'
    title.textContent = heading
    el.append(title)
    if (typeof data.intro === 'string' && data.intro) {
      for (const part of data.intro.split('\n\n')) {
        if (!part.trim()) continue
        const para = document.createElement('p')
        para.className = 'skin-viewer__intro'
        para.textContent = part.trim()
        el.append(para)
      }
    }
  }

  const stage = document.createElement('div')
  stage.className = 'skin-viewer__stage'

  // The fallback is the default state, not an error state: it is what a
  // crawler, a no-JS reader and a machine without WebGL all get, and it says
  // what the model says.
  const list = document.createElement('dl')
  list.className = 'skin-viewer__parts'
  for (const [term, description] of parts) {
    const dt = document.createElement('dt')
    dt.textContent = term
    const dd = document.createElement('dd')
    dd.textContent = description
    list.append(dt, dd)
  }
  stage.append(list)
  el.append(stage)

  if (options.anchor !== false) {
    claimAnchor(el, heading || 'The mount, exploded', heading ? 'In short' : 'CAD')
  }

  const caption = document.createElement('figcaption')
  caption.className = 'skin-viewer__caption'
  caption.textContent = String(
    data.caption ?? 'The mount in both versions. The change was screws to magnets.',
  )
  el.append(caption)

  // Controls sit inside the stage, over the model rather than under it. They
  // are markup, and inert until the scene is up: a button that rotates nothing
  // is worse than no button.
  const controls = document.createElement('div')
  controls.className = 'skin-viewer__controls'
  controls.hidden = true

  const button = (label: string, cls = ''): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    b.textContent = label
    if (cls) b.className = cls
    return b
  }
  // A group is a small labelled instrument: the word above, the controls
  // below. The label is what makes three different affordances read as
  // three different questions rather than seven grey buttons.
  const group = (label: string, cls: string, ...kids: HTMLElement[]): HTMLElement => {
    const g = document.createElement('div')
    g.className = `skin-viewer__group ${cls}`
    g.setAttribute('role', 'group')
    g.setAttribute('aria-label', label)
    const tag = document.createElement('span')
    tag.className = 'skin-viewer__group-label'
    tag.textContent = label
    const row = document.createElement('div')
    row.className = 'skin-viewer__row'
    row.append(...kids)
    g.append(tag, row)
    return g
  }

  // Named for what they are, not for what fastens them: the fastener story
  // is the part list's and the caption's to tell.
  const v1 = button('Version 1', 'skin-viewer__opt')
  const v2 = button('Version 2', 'skin-viewer__opt')
  const shell = button('Shell', 'skin-viewer__chip')
  const plate = button('Plate', 'skin-viewer__chip')
  const machine = button('Machine', 'skin-viewer__chip')
  const explode = button('Explode', 'skin-viewer__primary')
  const reset = button('Reset view', 'skin-viewer__ghost')

  controls.append(
    group('Version', 'skin-viewer__group--switch', v1, v2),
    group('Show', 'skin-viewer__group--parts', shell, plate, machine),
    group('View', 'skin-viewer__group--view', explode, reset),
  )
  stage.append(controls)

  const hint = document.createElement('p')
  hint.className = 'skin-viewer__hint'
  hint.textContent = 'Drag to turn · two fingers or right-drag to pan · scroll to zoom'
  stage.append(hint)

  void mount(stage, controls, { v1, v2, shell, plate, machine, explode, reset, list })

  return el
}

interface Controls {
  v1: HTMLButtonElement
  v2: HTMLButtonElement
  shell: HTMLButtonElement
  plate: HTMLButtonElement
  machine: HTMLButtonElement
  explode: HTMLButtonElement
  reset: HTMLButtonElement
  list: HTMLElement
}

async function mount(stage: HTMLElement, controls: HTMLElement, ui: Controls): Promise<void> {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Wait until it is worth the download.
  await new Promise<void>((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        observer.disconnect()
        resolve()
      },
      { rootMargin: '300px' },
    )
    observer.observe(stage)
  })

  let THREE: typeof import('three')
  let OrbitControls: typeof import('three/examples/jsm/controls/OrbitControls.js').OrbitControls
  try {
    const [three, orbit] = await Promise.all([
      import('three'),
      import('three/examples/jsm/controls/OrbitControls.js'),
    ])
    THREE = three
    OrbitControls = orbit.OrbitControls
  } catch {
    return // The part list stays. That is a complete answer.
  }

  const canvas = document.createElement('canvas')
  canvas.className = 'skin-viewer__canvas'

  let renderer: InstanceType<typeof THREE.WebGLRenderer>
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
  } catch {
    return
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.setClearColor(0x000000, 0)

  stage.append(canvas)
  // The list starts inside the stage because without WebGL it *is* the block.
  // Once the model is up it moves out and sits underneath as a specification,
  // rather than floating over the thing it describes.
  stage.after(ui.list)
  ui.list.classList.add('skin-viewer__parts--spec')
  controls.hidden = false

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(32, 1, 0.05, 40)
  camera.position.set(2.7, 1.9, 3.3)
  scene.add(new THREE.HemisphereLight(0xffffff, 0xdfe7ff, 0.75))
  const key = new THREE.DirectionalLight(0xffffff, 1.5)
  key.position.set(3, 5, 2)
  scene.add(key)

  // One surface for every printed part.
  //
  // The CAD arrived as STL, which carries no materials at all, and inventing
  // one per part would be describing a finish the geometry does not claim.
  // Everything printed shares this, so what you compare between parts is
  // shape. The magnets get a metal because they are not printed and reading
  // them as plastic would be the actual error.
  const matPart = new THREE.MeshStandardMaterial({ color: 0xc9ccd2, roughness: 0.62, metalness: 0 })
  const ghost = new THREE.MeshStandardMaterial({
    color: RED_PRINT,
    roughness: 0.5,
    transparent: true,
    opacity: 0.3,
  })

  // The approximate head, left visible as a wireframe. It is the idea the whole
  // mount comes from and it is invisible in the finished object.
  //
  // Modelled in Onshape as an oval rather than a sphere, so the wireframe is
  // squashed to match: a sphere here would illustrate a method nobody used.
  const matSphere = new THREE.MeshBasicMaterial({
    color: BLUE,
    wireframe: true,
    transparent: true,
    opacity: 0.16,
  })

  const root = new THREE.Group()
  scene.add(root)

  const v1Dome = new THREE.Group()
  const v2Dome = new THREE.Group()
  const plateGroup = new THREE.Group()
  const baseGroup = new THREE.Group()
  const sphereGroup = new THREE.Group()
  root.add(v1Dome, v2Dome, plateGroup, baseGroup, sphereGroup)

  // The real parts, from the CAD. Loaded in parallel and behind the same
  // observer as the rest of the viewer, so a reader who never scrolls this far
  // pays for none of it.
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
  const { MeshoptDecoder } = await import('three/examples/jsm/libs/meshopt_decoder.module.js')
  const loader = new GLTFLoader()
  loader.setMeshoptDecoder(MeshoptDecoder)

  const load = async (
    file: string,
    material: InstanceType<typeof THREE.Material>,
  ): Promise<InstanceType<typeof THREE.Object3D> | null> => {
    try {
      const gltf = await loader.loadAsync(asset(file))
      gltf.scene.traverse((child) => {
        const mesh = child as InstanceType<typeof THREE.Mesh>
        if (mesh.isMesh) mesh.material = material
      })
      return gltf.scene
    } catch {
      return null
    }
  }

  const [shellV1, shellV2, plate, machine] = await Promise.all([
    load('models/cad/HEADSHELL_v1.glb', matPart),
    load('models/cad/HEADSHELL_v2.glb', matPart),
    load('models/cad/BASE_v1.glb', matPart),
    load('models/bubble-machine.glb', ghost),
  ])

  // Centre each part on its own footprint and stand it on its own floor, then
  // stack them. The exports do not share an origin — they were saved from
  // different documents at different times — so trusting their coordinates
  // would pile them through each other. Measuring is the only thing that
  // survives someone re-exporting one of them.
  const measure = (o: InstanceType<typeof THREE.Object3D>) => {
    const box = new THREE.Box3().setFromObject(o)
    return { box, size: box.getSize(new THREE.Vector3()), mid: box.getCenter(new THREE.Vector3()) }
  }
  const seat = (o: InstanceType<typeof THREE.Object3D> | null, floor: number): number => {
    if (!o) return floor
    const { box, mid, size } = measure(o)
    o.position.set(-mid.x, floor - box.min.y, -mid.z)
    return floor + size.y
  }

  // Everything below is laid out in the CAD's own millimetres, and the whole
  // assembly is scaled once at the end.
  //
  // Scaling first is the trap: `measure` reads world space, so every size and
  // height it returns is already shrunk, while the positions written back are
  // local. Each placement then lands short by the scale factor, which put the
  // machine floating off the plate.
  const reference = shellV2 ?? shellV1

  let top = 0
  if (shellV1) { v1Dome.add(shellV1); top = seat(shellV1, 0) }
  if (shellV2) { v2Dome.add(shellV2); top = Math.max(top, seat(shellV2, 0)) }
  if (plate) { plateGroup.add(plate); seat(plate, top) }

  // No magnets in the assembly. They are hardware, not printed parts, and four
  // discs sitting on the post ring only clutter the one thing this view is for:
  // which printed piece goes where. The part list still names them, and the
  // difference between the two shells is visible in the shells themselves —
  // v2 carries the pockets.

  if (machine) {
    baseGroup.add(machine)
    const plateTop = plate ? measure(plate).box.max.y : top
    const m = measure(machine)
    // The machine is a scan in its own units; bring it to the plate's width.
    const wide = plate ? measure(plate).size.x : 1
    machine.scale.setScalar((wide * 1.16) / Math.max(m.size.x, 1e-6))
    const after = measure(machine)
    machine.position.set(-after.mid.x, plateTop - after.box.min.y, -after.mid.z)
  }

  // The head the shell was cut from. The shell is the cap off its crown, so
  // the oval hangs below with its top at the shell's top rather than sitting
  // centred on it.
  if (reference) {
    const { size } = measure(reference)
    const radius = size.z * 0.5
    // The crown only, not the whole head. A full oval is twice the height of
    // every part above it, and framing an assembly to contain it leaves the
    // hardware too small to read. The cap is also the honest half: this is the
    // part the shell was taken from, and the jaw was never in the model.
    const oval = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 28, 18, 0, Math.PI * 2, 0, Math.PI * 0.62),
      matSphere,
    )
    oval.scale.set(0.94, 0.96, 1)
    oval.position.y = top - radius * 0.96
    sphereGroup.add(oval)
  }

  // Scale last, when every part is placed and the assembly is one object.
  if (reference) {
    const { size } = measure(reference)
    root.scale.setScalar(1.25 / Math.max(size.x, size.z, 1e-6))
  }

  // Frame it from the assembly, not from constants.
  //
  // The stack is as tall as whatever the CAD happens to be, and it grows again
  // when it explodes, so a fixed camera position frames one arrangement and
  // clips every other. Centre the assembly on the origin, then place the
  // camera at the distance that contains its bounding sphere at the tighter of
  // the two field of view angles, with room for the exploded height.
  const whole = new THREE.Box3().setFromObject(root)
  const wholeMid = whole.getCenter(new THREE.Vector3())
  const wholeSize = whole.getSize(new THREE.Vector3())
  root.position.sub(wholeMid)

  // Half the assembly's diagonal, assembled and exploded. Framing for the
  // exploded height at all times leaves the assembled state small in its own
  // box for the sake of a state it is not in, so the distance follows the
  // state instead.
  // The separation, in the assembly's own local units.
  //
  // `wholeSize` is a world measurement and the group positions it drives are
  // local, inside a root scaled by about 1/120 — so the previous value shrank
  // by that factor on the way in and the parts moved a fraction of a
  // millimetre. That is why explode appeared to do nothing at all.
  const rootScale = root.scale.x || 1
  const lift = (wholeSize.y / rootScale) * 0.40
  const reachAt = (tall: number): number =>
    new THREE.Vector3(wholeSize.x, tall, wholeSize.z).length() / 2
  const reachClosed = reachAt(wholeSize.y)
  // The exploded height back in world units, plus the topmost part's own
  // height, which sits above the lift rather than inside it.
  const reachOpen = reachAt(wholeSize.y * 1.32 + lift * rootScale * 2.0)
  const frameAt = (aspect: number, reach: number): number => {
    const vertical = (camera.fov * Math.PI) / 180
    const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * aspect)
    return reach / Math.sin(Math.min(vertical, horizontal) / 2) * 1.04
  }
  camera.near = Math.max(0.01, reachClosed / 100)
  camera.far = reachOpen * 20

  const controlsOrbit = new OrbitControls(camera, canvas)
  // Home is the mount. The machine is the tallest thing in the stack, so an
  // origin-centred orbit opens on it — but the machine is the one part this
  // page did not make. The view opens lower, on the shell and the plate,
  // from just above their horizon.
  const homeTarget = new THREE.Vector3(0, -wholeSize.y * 0.2, 0)
  const homeDir = new THREE.Vector3(0.72, 0.26, 0.88).normalize()
  controlsOrbit.target.copy(homeTarget)
  // Everything on. This is the one place on the page a reader can handle the
  // parts, and taking pan and zoom away to protect a framing is protecting the
  // wrong thing.
  controlsOrbit.enableZoom = true
  controlsOrbit.enablePan = true
  controlsOrbit.zoomSpeed = 0.75
  controlsOrbit.autoRotate = true
  controlsOrbit.autoRotateSpeed = 0.9
  controlsOrbit.enableDamping = true
  controlsOrbit.dampingFactor = 0.06
  // Tilt from just above the horizon to nearly overhead: enough to look into
  // the plate and under the shell, stopping short of the poles where an orbit
  // camera flips.
  controlsOrbit.minPolarAngle = 0.18
  controlsOrbit.maxPolarAngle = 2.5
  // Auto-rotation is an invitation, not a behaviour to fight. First touch ends it.
  for (const stop of ['pointerdown', 'wheel'] as const) {
    canvas.addEventListener(stop, () => {
      controlsOrbit.autoRotate = false
      touched = true
    }, { passive: true })
  }

  let variant: 'v1' | 'v2' = 'v2'
  let exploded = false
  const want = { plate: 0, base: 0, sphere: 0 }
  let wantDistance = 0
  // Where the camera should be looking: the mount at rest, the middle of the
  // whole spread when exploded — an exploded stack aimed at the mount crops
  // the machine off the top of the frame.
  let wantTargetY = 0
  const shown = { shell: true, plate: true, machine: true }
  // Declared with the rest of the state, not next to `fit`. `apply` calls
  // `fit`, and `apply` runs before the bottom of this function, so a `let`
  // down there is still in its dead zone when the first frame is drawn.
  let touched = false

  function apply(): void {
    v1Dome.visible = shown.shell && variant === 'v1'
    v2Dome.visible = shown.shell && variant === 'v2'
    plateGroup.visible = shown.plate
    baseGroup.visible = shown.machine
    for (const [key, node] of [
      ['shell', ui.shell],
      ['plate', ui.plate],
      ['machine', ui.machine],
    ] as ['shell' | 'plate' | 'machine', HTMLButtonElement][]) {
      node.setAttribute('aria-pressed', String(shown[key]))
    }
    ui.v1.setAttribute('aria-pressed', String(variant === 'v1'))
    ui.v2.setAttribute('aria-pressed', String(variant === 'v2'))
    // Multiples of the assembly's own height, so the separation reads the
    // same whatever the parts are scaled to. Written as targets rather than
    // positions: the render loop carries each part there, so the stack is
    // seen coming apart in order rather than found already apart.
    want.plate = exploded ? lift : 0
    want.base = exploded ? lift * 2.0 : 0
    want.sphere = exploded ? -lift * 0.55 : 0
    wantTargetY = exploded ? wholeSize.y * 0.12 : homeTarget.y
    ui.explode.setAttribute('aria-pressed', String(exploded))
    ui.explode.textContent = exploded ? 'Assemble' : 'Explode'
    // Pull back for the taller arrangement, and only for it.
    fit()
  }
  ui.v1.addEventListener('click', () => {
    variant = 'v1'
    apply()
  })
  ui.v2.addEventListener('click', () => {
    variant = 'v2'
    apply()
  })
  ui.explode.addEventListener('click', () => {
    exploded = !exploded
    apply()
  })
  for (const [key, node] of [
    ['shell', ui.shell],
    ['plate', ui.plate],
    ['machine', ui.machine],
  ] as ['shell' | 'plate' | 'machine', HTMLButtonElement][]) {
    node.addEventListener('click', () => {
      shown[key] = !shown[key]
      apply()
    })
  }
  ui.reset.addEventListener('click', () => {
    touched = false
    controlsOrbit.target.copy(homeTarget)
    camera.position
      .copy(homeTarget)
      .addScaledVector(homeDir, frameAt(camera.aspect, exploded ? reachOpen : reachClosed))
    controlsOrbit.update()
  })
  apply()

  function fit(): void {
    // Measured from the canvas, not from the stage around it. On a narrow
    // screen the controls take the bottom of the stage for themselves and
    // the canvas is only the part above them; sizing the renderer to the
    // stage there would stretch the render behind the panel and put the
    // model's centre underneath it.
    const w = canvas.clientWidth || stage.clientWidth || 1
    const h = canvas.clientHeight || stage.clientHeight || 1
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    // Re-framed on every resize: a narrow stage is limited by its horizontal
    // angle, not its vertical one, and that is the case a fixed distance gets
    // wrong.
    const distance = frameAt(camera.aspect, exploded ? reachOpen : reachClosed)
    // The framing distance is an intent the render loop eases toward, so an
    // explode is a pull-back rather than a cut. It only steers an untouched
    // camera; once the reader has moved it, reframing is fighting them.
    wantDistance = distance
    // A range, not a clamp. Pinning min and max to the framing distance is
    // what disabled zoom; the reader can now come in close to a post or pull
    // back off the assembly, within reason.
    controlsOrbit.minDistance = distance * 0.28
    controlsOrbit.maxDistance = distance * 2.2
    camera.updateProjectionMatrix()
  }
  const resize = new ResizeObserver(fit)
  resize.observe(stage)
  resize.observe(canvas)
  fit()
  // The one snap: the camera has no position yet, and easing out from the
  // origin on first paint would read as a zoom nobody asked for.
  camera.position.copy(homeTarget).addScaledVector(homeDir, wantDistance)
  controlsOrbit.update()

  // Only render while it is on screen. An idle WebGL loop below the fold is
  // a battery drain for nothing.
  let visible = false
  const seen = new IntersectionObserver((entries) => {
    visible = entries[0]?.isIntersecting ?? false
  })
  seen.observe(stage)

  let raf = 0
  let last = performance.now()
  const frame = (now: number = performance.now()): void => {
    raf = requestAnimationFrame(frame)
    if (!visible) {
      last = now
      return
    }
    const dt = Math.min((now - last) / 1000, 0.05)
    last = now
    // Frame-rate independent exponential ease: most of the travel in the
    // first quarter second, settled inside a second, never a spring.
    const ease = 1 - Math.exp(-6.5 * dt)
    plateGroup.position.y += (want.plate - plateGroup.position.y) * ease
    baseGroup.position.y += (want.base - baseGroup.position.y) * ease
    sphereGroup.position.y += (want.sphere - sphereGroup.position.y) * ease
    if (!touched) {
      controlsOrbit.target.y += (wantTargetY - controlsOrbit.target.y) * ease
    }
    controlsOrbit.update()
    if (!touched && wantDistance > 0) {
      // Adjust only the orbit's length, after update so auto-rotation keeps
      // its heading. The direction stays the reader's; the reach is ours.
      const offset = camera.position.clone().sub(controlsOrbit.target)
      const length = offset.length() || 1
      const next = length + (wantDistance - length) * ease
      camera.position
        .copy(controlsOrbit.target)
        .addScaledVector(offset.multiplyScalar(1 / length), next)
    }
    renderer.render(scene, camera)
  }
  frame()

  window.addEventListener(
    'pagehide',
    () => {
      cancelAnimationFrame(raf)
      resize.disconnect()
      seen.disconnect()
      renderer.dispose()
    },
    { once: true },
  )
}
